import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { defaultColumnVisibility, EnergyColumnVisibility } from '../components/EnergyColumnDefinitions';
import { CHARGING_STATUSES } from '../components/ChargingStatusFilter';
import { ALLOCATED_RANGES } from '../components/AllocatedCurrentFilter';

export interface DashboardUIStore {
  // Selection state
  selectedLocation: string;
  selectedLocationActorId: string; // Empty string '' means "All Locations"
  shouldAutoScrollTree: boolean;
  skipLocationSelectorApiCall: boolean;

  // UI state
  activeTab: string;
  isRefreshing: boolean;
  columnVisibility: EnergyColumnVisibility;
  isTreeViewVisible: boolean;

  // Filter state
  selectedChargingStatuses: string[];
  selectedAllocatedRanges: string[];
  hideInactive: boolean;
  unitMode: 'A' | 'kW';
  // Tree view state
  isResizing: boolean;
  treeViewWidth: number;
  screenSize: number;

  // Grid expand/collapse state
  expandedNodes: string[]; // Array of node IDs that are expanded
  hasUserInteractedWithExpansion: boolean; // Track if user has manually expanded/collapsed

  // Actions
  setSelectedLocation: (location: string) => void;
  setSelectedLocationActorId: (actorId: string) => void;
  setShouldAutoScrollTree: (value: boolean) => void;
  setSkipLocationSelectorApiCall: (value: boolean) => void;
  setActiveTab: (tab: string) => void;
  setIsRefreshing: (value: boolean) => void;
  setColumnVisibility: (visibility: EnergyColumnVisibility) => void;
  setIsTreeViewVisible: (visible: boolean) => void;
  setIsResizing: (resizing: boolean) => void;
  setTreeViewWidth: (width: number) => void;
  setScreenSize: (size: number) => void;
  setSelectedChargingStatuses: (statuses: string[]) => void;
  setSelectedAllocatedRanges: (ranges: string[]) => void;
  setHideInactive: (hide: boolean) => void;
  setUnitMode: (mode: 'A' | 'kW') => void;
  setExpandedNodes: (nodeIds: string[]) => void;
  toggleNodeExpansion: (nodeId: string) => void;
  expandAllNodes: (nodeIds: string[]) => void;
  collapseAllNodes: () => void;
  resetExpansionState: () => void;

  // Computed/derived actions
  toggleTreeView: () => void;
  initializeTreeWidth: () => void;
  updateResponsiveWidth: (newScreenSize: number) => void;
}

export const useDashboardUIStore = create<DashboardUIStore>()(
  persist(
    (set, get) => ({
      // Initial state
      selectedLocation: '',
      selectedLocationActorId: '', // Start with "All Locations" by default
      shouldAutoScrollTree: false,
      skipLocationSelectorApiCall: false,
      activeTab: 'Realtime Usage',
      isRefreshing: false,
      columnVisibility: defaultColumnVisibility,
      isTreeViewVisible: false,
      selectedChargingStatuses: [...CHARGING_STATUSES],
      selectedAllocatedRanges: ALLOCATED_RANGES.map(r => r.id),
      hideInactive: true,
      unitMode: 'A' as const,
      isResizing: false,
      treeViewWidth: 280,
      screenSize: typeof window !== 'undefined' ? window.innerWidth : 1024,
      expandedNodes: [], // Initially empty, will be populated on first load
      hasUserInteractedWithExpansion: false, // Track manual interaction

      // Basic setters
      setSelectedLocation: (location) => set({ selectedLocation: location }),
      setSelectedLocationActorId: (actorId) => set({ selectedLocationActorId: actorId }),
      setShouldAutoScrollTree: (value) => set({ shouldAutoScrollTree: value }),
      setSkipLocationSelectorApiCall: (value) => set({ skipLocationSelectorApiCall: value }),
      setActiveTab: (tab) => set({ activeTab: tab }),
      setIsRefreshing: (value) => set({ isRefreshing: value }),
      setColumnVisibility: (visibility) => set({ columnVisibility: visibility }),
      setIsTreeViewVisible: (visible) => set({ isTreeViewVisible: visible }),
      setIsResizing: (resizing) => set({ isResizing: resizing }),
      setTreeViewWidth: (width) => set({ treeViewWidth: width }),
      setScreenSize: (size) => set({ screenSize: size }),
      setSelectedChargingStatuses: (statuses) => set({ selectedChargingStatuses: statuses }),
      setSelectedAllocatedRanges: (ranges) => set({ selectedAllocatedRanges: ranges }),
      setHideInactive: (hide) => set({ hideInactive: hide }),
      setUnitMode: (mode) => set({ unitMode: mode }),
      // Expand/collapse actions
      setExpandedNodes: (nodeIds) => set({ expandedNodes: nodeIds }),
      toggleNodeExpansion: (nodeId) => {
        const { expandedNodes } = get();
        const isExpanded = expandedNodes.includes(nodeId);
        if (isExpanded) {
          // Collapse: remove from array
          set({ expandedNodes: expandedNodes.filter(id => id !== nodeId), hasUserInteractedWithExpansion: true });
        } else {
          // Expand: add to array
          set({ expandedNodes: [...expandedNodes, nodeId], hasUserInteractedWithExpansion: true });
        }
      },
      expandAllNodes: (nodeIds) => set({ expandedNodes: nodeIds, hasUserInteractedWithExpansion: true }),
      collapseAllNodes: () => set({ expandedNodes: [], hasUserInteractedWithExpansion: true }),
      resetExpansionState: () => set({ expandedNodes: [], hasUserInteractedWithExpansion: false }),

      // Computed actions
      toggleTreeView: () => {
        const { isTreeViewVisible } = get();
        set({ isTreeViewVisible: !isTreeViewVisible });
      },

      initializeTreeWidth: () => {
        if (typeof window !== 'undefined') {
          const screenWidth = window.innerWidth;
          let width = 280;

          if (screenWidth < 1200) {
            width = Math.min(250, screenWidth * 0.25);
          } else if (screenWidth < 1440) {
            width = 320;
          } else {
            width = 400;
          }

          set({
            treeViewWidth: width,
            screenSize: screenWidth
          });
        }
      },

      updateResponsiveWidth: (newScreenSize: number) => {
        const { treeViewWidth } = get();
        
        // Adjust tree width if it's too large for the new screen size
        const maxWidthPercent = newScreenSize < 1200 ? 0.25 : 0.4;
        const maxWidth = Math.floor(newScreenSize * maxWidthPercent);
        
        if (treeViewWidth > maxWidth) {
          set({ treeViewWidth: maxWidth });
        }
        
        set({ screenSize: newScreenSize });
      },
    }),
    {
      name: 'dashboard-ui-storage-v12',
      partialize: (state) => ({
        // Persist only UI preferences
        activeTab: state.activeTab,
        columnVisibility: state.columnVisibility,
        isTreeViewVisible: state.isTreeViewVisible,
        treeViewWidth: state.treeViewWidth,
        selectedAllocatedRanges: state.selectedAllocatedRanges,
        hideInactive: state.hideInactive,
        unitMode: state.unitMode,
        expandedNodes: state.expandedNodes,
        hasUserInteractedWithExpansion: state.hasUserInteractedWithExpansion,
      }),
    }
  )
);